// Dialog — accessible modal primitive for the admin UI.
//
// ARIA contract:
//   role="dialog", aria-modal="true", aria-labelledby, aria-describedby.
//
// Focus behaviour:
//   - On open, focus moves into the dialog (first focusable element, or
//     the dialog container when nothing is focusable).
//   - Tab / Shift+Tab cycle within the dialog (focus trap).
//   - On close, focus returns to the element that opened the dialog
//     (the `document.activeElement` at the moment `open` flipped to true).
//
// Dismissal:
//   - Esc key calls `onClose`.
//   - Clicking the overlay (not the dialog card) calls `onClose`.
//
// A11y isolation of the page behind the overlay:
//   While open, every sibling of the dialog root at the body level has
//   BOTH `inert` and `aria-hidden="true"` applied. On close, we restore
//   whatever the sibling had before we touched it (so pre-existing
//   `aria-hidden` on e.g. a third-party widget is preserved).
//
//   Why both? `inert` is the real answer in the browser — it disables
//   focus, pointer, and AT entirely. But jsdom (Vitest's DOM) does not
//   implement `inert` semantics, so our unit tests would see focus
//   "leak" to the page behind. Setting `aria-hidden="true"` alongside
//   gives AT the correct signal even on engines that ignore `inert`,
//   and lets our tests assert a11y isolation via queryable attributes
//   rather than the runtime focus effect of `inert`. See issue #52.
//
// Portal to document.body: this primitive is shared across the admin
// shell, so it must be able to isolate the sidebar and any page
// content mounted outside the caller's subtree. Rendering into a
// body-level host keeps the sibling isolation scope honest.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children?: ReactNode
  /**
   * Optional label for the close-on-overlay-click behaviour.
   * Defaults to true. Pass `false` to require explicit Esc / button
   * dismissal (e.g. destructive confirmations).
   */
  dismissOnOverlayClick?: boolean
  /**
   * `default` (current admin chrome — rounded-lg white card with a
   * built-in h2 title and p-6 padding) or `bare` (focus-trap + portal +
   * overlay shell only — caller renders the entire visible card via
   * `children`). The capture-flow add-asset / create-variant modals
   * use `bare` to render the Citycare-branded card chrome the field
   * design specifies; admin uses `default` everywhere.
   *
   * In `bare` mode, the title and (optional) description are still
   * rendered into the DOM as `sr-only` so `aria-labelledby` and
   * `aria-describedby` remain authoritative for screen readers — but
   * the visible heading is the caller's responsibility.
   */
  variant?: 'default' | 'bare'
  /**
   * Override classes for the overlay div. Used by `bare` capture
   * modals to tint the backdrop with the forest brand colour
   * (`bg-cc-forest/55 backdrop-blur-[2px]`) instead of the admin
   * `bg-black/50` default.
   */
  overlayClassName?: string
}

// Selector for elements that can receive focus. Intentionally conservative:
// buttons, links with href, inputs/selects/textareas, and anything with a
// non-negative tabindex.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableWithin(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  return Array.from(nodes).filter((el) => {
    // Exclude elements that are themselves inert or hidden. jsdom does not
    // compute these reliably, but we still try — the focus trap tests do
    // not depend on this filter.
    if (el.hasAttribute('disabled')) return false
    if (el.getAttribute('aria-hidden') === 'true') return false
    return true
  })
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  dismissOnOverlayClick = true,
  variant = 'default',
  overlayClassName,
}: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  // Lazily create the portal host once per component instance via
  // useState's init callback. This keeps the host stable across
  // renders without reading/writing a ref during render, which the
  // `react-hooks/refs` lint rule disallows.
  const [portalHost] = useState<HTMLDivElement | null>(() => {
    if (typeof document === 'undefined') return null
    const host = document.createElement('div')
    host.setAttribute('data-dialog-root', '')
    return host
  })
  // Element that had focus at the moment the dialog opened; focus returns
  // here on close.
  const invokerRef = useRef<HTMLElement | null>(null)

  // Memoise onClose so the Esc effect's dep array is stable even when the
  // parent inlines a new closure each render. (react-hooks/exhaustive-deps
  // requires it in the array.)
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Attach the portal host to document.body only while open.
  useEffect(() => {
    if (!open) return undefined
    const host = portalHost
    if (host === null) return undefined
    if (!document.body.contains(host)) {
      document.body.appendChild(host)
    }
    return () => {
      if (document.body.contains(host)) {
        document.body.removeChild(host)
      }
    }
  }, [open, portalHost])

  // Focus management + sibling inert/aria-hidden toggling.
  useEffect(() => {
    if (!open) return undefined

    // Capture the invoker at the moment we open.
    const previouslyFocused = document.activeElement
    invokerRef.current =
      previouslyFocused instanceof HTMLElement ? previouslyFocused : null

    const root = portalHost
    const dialog = dialogRef.current
    if (root === null || dialog === null) return undefined

    type SiblingSnapshot = {
      el: HTMLElement
      hadInert: boolean
      prevAriaHidden: string | null
    }
    const touched: SiblingSnapshot[] = []
    for (const child of Array.from(document.body.children)) {
      if (child === root) continue
      if (!(child instanceof HTMLElement)) continue
      touched.push({
        el: child,
        hadInert: child.hasAttribute('inert'),
        prevAriaHidden: child.getAttribute('aria-hidden'),
      })
      child.setAttribute('inert', '')
      child.setAttribute('aria-hidden', 'true')
    }

    // Move focus into the dialog. Prefer the first focusable descendant;
    // fall back to the dialog container itself so keyboard users are not
    // stranded on the page behind.
    const focusables = getFocusableWithin(dialog)
    const firstFocusable = focusables[0]
    if (firstFocusable !== undefined) {
      firstFocusable.focus()
    } else {
      dialog.focus()
    }

    return () => {
      for (const snap of touched) {
        if (!snap.hadInert) snap.el.removeAttribute('inert')
        if (snap.prevAriaHidden === null) {
          snap.el.removeAttribute('aria-hidden')
        } else {
          snap.el.setAttribute('aria-hidden', snap.prevAriaHidden)
        }
      }
      // Return focus to the invoker.
      const invoker = invokerRef.current
      if (invoker !== null && document.contains(invoker)) {
        invoker.focus()
      }
      invokerRef.current = null
    }
  }, [open, portalHost])

  // Global Esc listener (document-level so it fires even when focus is on
  // the dialog container rather than a focusable child).
  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        handleClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, handleClose])

  if (!open || portalHost === null) return null

  // Focus-trap: intercept Tab / Shift+Tab and wrap. Using onKeyDown on the
  // dialog root keeps the trap scoped to the dialog subtree.
  const handleDialogKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const dialog = dialogRef.current
    if (dialog === null) return
    const focusables = getFocusableWithin(dialog)
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (first === undefined || last === undefined) {
      e.preventDefault()
      dialog.focus()
      return
    }
    const active = document.activeElement
    if (e.shiftKey) {
      if (active === first || !dialog.contains(active)) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  const handleOverlayClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    // Only dismiss when the click lands on the overlay itself, not on a
    // child (the dialog card). React synthesises click events bubbling
    // from inside the card, so we must check target === currentTarget.
    if (!dismissOnOverlayClick) return
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const overlayClasses =
    overlayClassName ?? 'fixed inset-0 z-40 bg-black/50'

  if (variant === 'bare') {
    // Bare mode: caller renders the entire visible card. We still keep
    // an authoritative <h2 sr-only> + <p sr-only> in the DOM so
    // `aria-labelledby` / `aria-describedby` resolve for screen
    // readers, but the dialog div itself is just a transparent
    // viewport-filling shell with `pointer-events-none` so clicks on
    // empty space pass through to the overlay (and dismiss). The
    // children wrapper restores `pointer-events-auto` so the card
    // remains interactive.
    return createPortal(
      <>
        <div
          data-testid="dialog-overlay"
          onClick={handleOverlayClick}
          className={overlayClasses}
        />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={
            description !== undefined ? descriptionId : undefined
          }
          tabIndex={-1}
          onKeyDown={handleDialogKeyDown}
          className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center px-4 pt-[5vh] pb-[5vh] focus:outline-none"
        >
          <h2 id={titleId} className="sr-only">
            {title}
          </h2>
          {description !== undefined ? (
            <p id={descriptionId} className="sr-only">
              {description}
            </p>
          ) : null}
          {children !== undefined ? (
            <div className="pointer-events-auto">{children}</div>
          ) : null}
        </div>
      </>,
      portalHost,
    )
  }

  return createPortal(
    <>
      <div
        data-testid="dialog-overlay"
        onClick={handleOverlayClick}
        className={overlayClasses}
      />
      {/*
        max-h: dvh (dynamic viewport) not vh — iOS Safari + Android
        Chrome compute `100vh` against the LARGE viewport (URL bar
        hidden), so a vh-based cap can still push the bottom of the
        card below the visible fold when the URL bar is on screen.
        `dvh` tracks the actual visible viewport and resizes when the
        URL bar shows/hides. Matches the proven cap on the capture
        flow's add-asset modal (see add-asset-dialog.tsx).
        overflow-y-auto lets tall content (e.g. the asset-type create
        form with UoM + grouping selects) scroll inside the card on
        short viewports instead of clipping the bottom actions.
      */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
        className="fixed left-1/2 top-1/2 z-50 flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-white p-6 shadow-xl focus:outline-none"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          {title}
        </h2>
        {description !== undefined ? (
          <p id={descriptionId} className="mt-1 text-sm text-gray-600">
            {description}
          </p>
        ) : null}
        {children !== undefined ? (
          <div className="mt-4">{children}</div>
        ) : null}
      </div>
    </>,
    portalHost,
  )
}
